import React, { useReducer, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { updateAppointments } from 'Features/services/AppointmentUpdate.service';
import { infoToastAction, warningToastAction } from 'utils';
import fields, { getUpdateFields } from './Field.constants';
import {hasValue} from 'utils';
import getUpdatedData from './utils/getUpdatedData';
import getPatchUpdate from './utils/getPatchUpdate';
import scanOnHold from './utils/scanOnHold';
import contractDateUpdatesAreInvalid from './utils/contractDateUpdatesAreInvalid';
import { updateReadyForApproval, updateReleaseDate } from './utils/isUpdating';
import reducer from './store/updater.reducer';
import initialState from './store/initialState';
import Actions from './store/updater.actions';
import viewUpdates from './ViewUpdate.constants';
import viewTypes from './ViewType.constants';
import eMarkingDateUpdatesAreInvalid from './utils/eMarkingDateUpdatesAreInvalid';

import View from './Updater.view';

const Updater = ({
    disabled,
    updaterOpen,
    setUpdaterOpen,
    year,
    grouping,
    inPreview,
    setInPreview,
    isFiltering,
    refinedAppointments,
    setDisplayAppointments,
    viewDataOpen,
    setViewDataOpen,
    startNewUpdate,
    isMarker = false,
    updateDataTable,
    saveData,
    displayData,
    updateViewType = ''
}) => {
    const updateFields = getUpdateFields(isMarker, updateViewType);
    const [state, dispatch] = useReducer(reducer, {...initialState, updateOptions: updateFields});
    const globalDispatch = useDispatch();
    const { isSupervisor } = useSelector(globalState => globalState.user);
    const { rfaSomeOnHold, rfaAllOnHold } = useMemo(() => scanOnHold(refinedAppointments), [refinedAppointments]);
    const {
        updateOptions,
        updates,
        hasUpdates,
        isSelecting,
        selectedUpdater,
        saveInvoked,
        updatedAll,
        updatedIds,
        failedIds,
        showModal
    } = state;

    const allUpdatersHaveValues = updates.reduce((result, currentUpdate) => {
        return result && hasValue(currentUpdate.value);
    }, true);

    const getFailMessage = () => {
        if (!saveInvoked || updatedAll) {
            return '';
        }
        if (updatedIds.length === 0) {
            return 'Unable to update any appointments';
        }
        return `Updated ${updatedIds.length} out of a total of ${updatedIds.length + failedIds.length} appointments`;
    };

    const savedAll = saveInvoked && updatedAll;
    const savedSome = saveInvoked && !updatedAll && updatedIds.length > 0;
    const successMessage = savedAll ? `Updated ${updatedIds.length} appointment${updatedIds.length > 1 ? 's': ''} successfully` : '';
    const failMessage = getFailMessage();

    const showAddIcon = !saveInvoked && !inPreview && !isSelecting && updateOptions.length > 0 && allUpdatersHaveValues;
    const showCancelButton = !savedAll && !inPreview && hasUpdates && updates.length > 0;
    const showPreviewButton = !savedAll && hasUpdates && !inPreview;
    const showSaveButton = !savedAll && hasUpdates && inPreview;
    const showRemovePreviewButton = inPreview && !savedAll;

    useEffect(() => {
        if (refinedAppointments.length > 0 && updateReadyForApproval(updates)) {
            let errorMsg = '';
            if (rfaSomeOnHold) {
                errorMsg = 'Removing Ready for Approval Updater as cannot update Ready for Approval when some appointments are On Hold';
            }

            if (rfaAllOnHold && !isSupervisor) {
                errorMsg = 'Removing Ready for Approval Updater as updating Ready For Approval from On Hold requires a Supervisor';
            }

            if (errorMsg !== '') {
                handleRemoveUpdater(fields.ReadyForApproval);
                globalDispatch(warningToastAction(errorMsg));
            }
        }
    // eslint-disable-next-line
    }, [refinedAppointments]);

    useEffect(() => {
        if (isFiltering) {
            clearAll();
        }
    // eslint-disable-next-line
    }, [isFiltering]);

    const handleAddSelector = () => {
        if (updateOptions.length > 1) {
            dispatch({ type: Actions.ADD_SELECTOR });
        } else if (updateOptions.length === 1) {
            dispatch({ type: Actions.ADD_UPDATER, payload: { field: updateOptions[0] }});
        }
    };

    const handleRemoveSelector = () => {
        dispatch({ type: Actions.REMOVE_SELECTOR });
    };

    const handleAddUpdater = event => {
        const field = event.target.value;
        if (field === fields.ReadyForApproval) {
            let errorMsg = '';
            if (rfaSomeOnHold) {
                errorMsg = 'Cannot update Ready for Approval when some appointments are On Hold, either All or None should be On Hold.';
            }

            if (rfaAllOnHold && !isSupervisor) {
                errorMsg = 'Updating Ready For Approval from On Hold requires a Supervisor';
            }

            if (errorMsg !== '') {
                globalDispatch(infoToastAction(errorMsg));
                return;
            }
        }
        dispatch({ type: Actions.ADD_UPDATER, payload: { field }});
    };

    const handleRemoveUpdater = field => () => {
        dispatch({ type: Actions.REMOVE_UPDATER, payload: { field }});
    }

    const handleFieldChange = field => value => {
        dispatch({type: Actions.FIELD_VALUE_CHANGED, payload: { field, value }});
    }

    const handlePreview = () => {
        const displayName = 'Preview';        
        setInPreview(true);
        
        if(isMarker) {
            updateDataTable && updateDataTable(updates, displayName);
        }
        else {        
            const updatedData = getUpdatedData(refinedAppointments, updates);
            updatedData.name = displayName;
            setDisplayAppointments && setDisplayAppointments(updatedData);
        }

        if (!viewDataOpen) {
            setViewDataOpen(true);
        }
    };

    const handleRemovePreview = () => {
        if (inPreview) {          
            setInPreview(false);

            if(isMarker) {
                updateDataTable && updateDataTable();
                return;
            }

            handleViewRefined();
        }
    };

    const handleViewRefined = () => {
        if(isMarker) {
            displayData && displayData(viewUpdates.Refined);
            return;
        }

        setDisplayAppointments && setDisplayAppointments(refinedAppointments);
    };

    const handleSave = async () => {
        let errorMsg = '';
        if (updateReadyForApproval(updates)) {
            if (rfaSomeOnHold) {
                errorMsg = 'Unable to Save as cannot update Ready for Approval when some appointments are On Hold. Either none or all should be On Hold';
            }

            if (rfaAllOnHold && !isSupervisor) {
                errorMsg = 'Unable to Save as updating Ready For Approval from On Hold requires a Supervisor';
            }

            if (rfaAllOnHold && isSupervisor && !updateReleaseDate(updates)) {
                errorMsg = 'Unable to Save, when updating Ready for Approval from On Hold, you need to also update the Contract Release Date.';
            }
        }

        if (contractDateUpdatesAreInvalid(refinedAppointments, updates)) {
            errorMsg = 'Unable to Save as Contract End Date must always be after Contract Release Date.';
        }

        if (isMarker && updateViewType && updateViewType == viewTypes.ScriptDates && eMarkingDateUpdatesAreInvalid(refinedAppointments, updates)) {
            errorMsg = 'Unable to Save as E-Marking End Date must always be after E-Marking Start Date.';
        }

        if (errorMsg !== '') {
            globalDispatch(warningToastAction(errorMsg));
            return;
        }

        let result = { updatedAll: false, updatedIds: [], failedIds: [] }
        if (isMarker) {
            result = await saveData();
        }
        else
        {
            const patchUpdate = getPatchUpdate(updates);
            result = await updateAppointments(year, grouping, refinedAppointments, patchUpdate);
        }
        dispatch({type: Actions.SAVE, payload: result });
    };

    const handleCancel = () => {
        dispatch({ type: Actions.CANCEL_REQUEST });
    };

    const handleCancelWithdrawn = () => {
        dispatch({ type: Actions.CLOSE_MODAL })
    };

    const handleCancelConfirmed = () => {
        clearAll();
        setUpdaterOpen();
    };

    const clearAll = () => {
        handleRemovePreview();
        dispatch({ type: Actions.CLEAR, payload: { updateFields } });
    };

    const handleStartNew = () => {
        clearAll();
        startNewUpdate();
    };

    const handleViewSaved = () => {
        const displayName = 'Saved';        
        if (inPreview) {
            setInPreview(false);
        }

        if(isMarker) {
            displayData && displayData(viewUpdates.PartialSaved, displayName);
            return;
        }

        const saved = refinedAppointments.filter(appt => updatedIds.includes(appt.appointmentId));
        saved.name = displayName;
        setDisplayAppointments && setDisplayAppointments(saved);
    };

    const handleViewFailed = () => {
        const displayName = 'Failed to save';        
        if (inPreview) {
            setInPreview(false);
        }

        if(isMarker) {
            displayData && displayData(viewUpdates.Failed, displayName);
            return;
        }

        const failed = refinedAppointments.filter(appt => failedIds.includes(appt.appointmentId));
        failed.name = displayName;
        setDisplayAppointments && setDisplayAppointments(failed);
    };

    return (
        <View
            disabled={disabled}
            open={updaterOpen}
            isSelecting={isSelecting}
            inPreview={inPreview}
            saveInvoked={saveInvoked}
            updatedAll={updatedAll}
            showModal={showModal}

            savedAll={savedAll}
            savedSome={savedSome}
            showAddIcon={showAddIcon}
            showCancelButton={showCancelButton}
            showPreviewButton={showPreviewButton}
            showSaveButton={showSaveButton}
            showRemovePreviewButton={showRemovePreviewButton}

            selectedUpdater={selectedUpdater}
            updateOptions={updateOptions}
            updates={updates}

            togglePanel={setUpdaterOpen}
            handleAddSelector={handleAddSelector}
            handleRemoveSelector={handleRemoveSelector}
            handleAddUpdater={handleAddUpdater}
            handleRemoveUpdater={handleRemoveUpdater}
            handleFieldChange={handleFieldChange}
            handlePreview={handlePreview}
            handleRemovePreview={handleRemovePreview}

            handleSave={handleSave}
            handleCancel={handleCancel}
            handleCancelWithdrawn={handleCancelWithdrawn}
            handleCancelConfirmed={handleCancelConfirmed}
            handleStartNew={handleStartNew}
            handleViewSaved={handleViewSaved}
            handleViewFailed={handleViewFailed}
            handleViewRefined={handleViewRefined}
            successMessage={successMessage}
            failMessage={failMessage}
            year={year}
        />
    );
};

Updater.propTypes = {
    disabled: PropTypes.bool,
    inPreview: PropTypes.bool.isRequired,
    setInPreview: PropTypes.func.isRequired,
    year: PropTypes.string,
    grouping: PropTypes.string.isRequired,
    isFiltering: PropTypes.bool.isRequired,
    updaterOpen: PropTypes.bool.isRequired,
    setUpdaterOpen: PropTypes.func.isRequired,
    refinedAppointments: PropTypes.arrayOf(PropTypes.object).isRequired,
    setDisplayAppointments: PropTypes.func,
    viewDataOpen: PropTypes.bool.isRequired,
    setViewDataOpen: PropTypes.func.isRequired,
    startNewUpdate: PropTypes.func.isRequired
}

export default Updater;